// The viewer's side of the translations (docs/i18n.md): the control panel,
// the controls hint and the labels main.js writes into them. The language
// comes from ?lang= (the site's pages link to /viewer/?lang=es), which is
// kept in localStorage `ss.lang` for later visits, else English. The
// browser's own language is not used while the viewer is only partly
// translated: a Spanish reader who has not asked for Spanish gets the
// English viewer they would have had before.
//
// The catalogues are bundled (a few KB each), so vt() is synchronous and a
// label is never written twice. The markup stays English; elements carry
//   data-i18n="key"             their text
//   data-i18n-title="key"       title
//   data-i18n-aria-label="key"  aria-label
// and applyViewerTranslations() fills them once at startup. English skips
// the walk, so the English viewer is the page as written.

import { createTranslator } from './translate.js';
import { DEFAULT_LOCALE, LOCALES, LOCALE_CODES, negotiateLocale } from './config.js';
import en from './locales/en/viewer.json';
import es from './locales/es/viewer.json';
import ptBR from './locales/pt-BR/viewer.json';
import hi from './locales/hi/viewer.json';
import zhHans from './locales/zh-Hans/viewer.json';
import id from './locales/id/viewer.json';

const RESOURCES = {
  en: { viewer: en },
  es: { viewer: es },
  'pt-BR': { viewer: ptBR },
  hi: { viewer: hi },
  'zh-Hans': { viewer: zhHans },
  id: { viewer: id },
};
const STORE_KEY = 'ss.lang';
const ATTRIBUTES = ['title', 'aria-label', 'placeholder'];

function readStored() {
  try { return localStorage.getItem(STORE_KEY); } catch { return null; }
}

function store(code) {
  try {
    if (code === DEFAULT_LOCALE) localStorage.removeItem(STORE_KEY);
    else localStorage.setItem(STORE_KEY, code);
  } catch { /* private window: the choice lasts for this page */ }
}

// ?lang= wins and is remembered (?lang=en goes back to English for good);
// then the remembered choice; then English.
export function pickViewerLocale(search = typeof location !== 'undefined' ? location.search : '', stored = readStored()) {
  const asked = new URLSearchParams(search).get('lang');
  if (asked) {
    const code = negotiateLocale(asked, LOCALE_CODES, null);
    if (code) {
      store(code);
      return code;
    }
  }
  return negotiateLocale(stored, LOCALE_CODES, DEFAULT_LOCALE);
}

export const VIEWER_LOCALE = pickViewerLocale();

// Another language's viewer messages, for the language offer
// (src/ui/langoffer.js), which speaks the language it offers.
export const viewerMessages = (code) => (RESOURCES[code] || RESOURCES[DEFAULT_LOCALE]).viewer;

export const vt = createTranslator({
  locale: VIEWER_LOCALE,
  resources: RESOURCES,
  namespaces: ['viewer'],
  escape: false,
  onMissing: ({ locale, key }) => console.warn(`[i18n] no ${locale} message for ${key}`),
});

// A number in the viewer's digits (Intl.NumberFormat options), formatters
// kept for reuse: vnum(4.2, { maximumFractionDigits: 1 }) -> '4,2' in es.
const numberFormats = new Map();
export function vnum(value, options = {}) {
  const key = JSON.stringify(options);
  if (!numberFormats.has(key)) numberFormats.set(key, new Intl.NumberFormat(VIEWER_LOCALE, options));
  return numberFormats.get(key).format(value);
}

// Fill every marked element under root. Safe to call again on markup added
// later (the satellites row builds its own button).
export function applyViewerTranslations(root = typeof document !== 'undefined' ? document : null) {
  if (!root || VIEWER_LOCALE === DEFAULT_LOCALE) return;
  if (root === document) document.documentElement.lang = VIEWER_LOCALE;
  for (const el of root.querySelectorAll('[data-i18n]')) el.textContent = vt(el.dataset.i18n);
  for (const name of ATTRIBUTES) {
    for (const el of root.querySelectorAll(`[data-i18n-${name}]`)) el.setAttribute(name, vt(el.getAttribute(`data-i18n-${name}`)));
  }
  // The closed groups' "2 on" counts: the inline script in index.html reads
  // its words from these and re-counts on this event.
  for (const el of root.querySelectorAll('.control-group-count')) {
    el.dataset.onOne = vt('controls.groupOn', { count: 1 });
    el.dataset.onOther = vt('controls.groupOn', { count: 2 });
  }
  document.dispatchEvent(new CustomEvent('viewer:relabel'));
}

// The language links at the foot of the More group (#viewer-languages):
// one per language in config.js other than the one showing, each keeping
// the place (the hash) and the rest of the query. A language added to the
// config gets its link here with no markup to write.
const GLOBE = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M3 12h18"></path><path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18"></path></svg>';

export function initViewerLanguageLinks(row = typeof document !== 'undefined' ? document.getElementById('viewer-languages') : null) {
  if (!row) return;
  const hrefFor = (code) => {
    const params = new URLSearchParams(location.search);
    params.set('lang', code);
    return `${location.pathname}?${params}${location.hash}`;
  };
  for (const { code, hreflang, name } of LOCALES) {
    if (code === VIEWER_LOCALE) continue;
    const a = document.createElement('a');
    a.className = 'audio-btn';
    a.lang = code;
    a.hreflang = hreflang;
    a.dataset.viewerLang = code;
    a.title = vt('controls.language.title', { language: name });
    a.innerHTML = GLOBE;
    const label = document.createElement('span');
    label.textContent = name;
    a.append(label);
    a.href = hrefFor(code);
    // The hash moves as the camera does, so the link is made at the click.
    a.addEventListener('pointerdown', () => { a.href = hrefFor(code); });
    a.addEventListener('focus', () => { a.href = hrefFor(code); });
    row.append(a);
  }
  row.hidden = !row.children.length;
}
